import React, { useEffect, useId, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import MegaphoneIcon from '@atlaskit/icon/core/megaphone';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useNotificationState } from '@confluence/flags/entry-points/useNotificationState.ts';

const FEEDBACK_DELAY_MS = 3000;

const messages = defineMessages({
	title: {
		id: 'byline-connections.feedback.flag.title',
		defaultMessage: 'Help us improve the Context Hub',
		description: 'Title of the flag inviting feedback after using the Context connections menu.',
	},
	description: {
		id: 'byline-connections.feedback.flag.description',
		defaultMessage: 'We’d love your feedback. This quick survey should take less than a minute.',
		description: 'Description of the invitation to give feedback on the Context connections menu.',
	},
	shareFeedback: {
		id: 'byline-connections.feedback.flag.share-feedback',
		defaultMessage: 'Share feedback',
		description: 'Action on the Context feedback invitation to start giving feedback.',
	},
});

type ConnectionsFeedbackFlagOptions = {
	contentId: string;
	isMenuReady: boolean;
	flags: Pick<FlagsStateContainer, 'showFlag' | 'hideFlag'>;
	/** Handoff after dismissing the invitation; the caller owns the feedback modal. */
	onShareFeedback?: () => void;
};

export const useConnectionsFeedbackFlag = ({
	contentId,
	isMenuReady,
	flags,
	onShareFeedback,
}: ConnectionsFeedbackFlagOptions): void => {
	const { formatMessage } = useIntl();
	const canShowNotifications = useNotificationState();
	const instanceId = useId();
	const flagId = `connections-byline-feedback-${contentId}-${instanceId}`;
	const visitRef = useRef({ shown: false, active: true });
	const callbacksRef = useRef({ formatMessage, onShareFeedback });

	useEffect(() => {
		callbacksRef.current = { formatMessage, onShareFeedback };
	}, [formatMessage, onShareFeedback]);

	useEffect(() => {
		const visit = { shown: false, active: true };
		visitRef.current = visit;
		return () => {
			visit.active = false;
			if (visit.shown) {
				void flags.hideFlag(flagId);
			}
		};
	}, [flagId, flags]);

	useEffect(() => {
		const visit = visitRef.current;
		if (!isMenuReady || !canShowNotifications || visit.shown) {
			return;
		}

		const timeout = window.setTimeout(() => {
			visit.shown = true;
			const { formatMessage } = callbacksRef.current;
			void flags
				.showFlag({
					id: flagId,
					type: 'custom',
					customIcon: <MegaphoneIcon label="" color="currentColor" spacing="spacious" />,
					title: formatMessage(messages.title),
					description: formatMessage(messages.description),
					actions: [
						{
							content: formatMessage(messages.shareFeedback),
							onClick: () => {
								void flags.hideFlag(flagId);
								callbacksRef.current.onShareFeedback?.();
							},
						},
					],
				})
				.then(() => {
					// The shared flag service can queue display behind another flag update.
					if (!visit.active) {
						void flags.hideFlag(flagId);
					}
				});
		}, FEEDBACK_DELAY_MS);

		return () => window.clearTimeout(timeout);
	}, [canShowNotifications, flagId, flags, isMenuReady]);
};
